import { TaskInstruction } from "pages/project/task/task.typing";

/**
 * Accordion values — one panel per category (Jira mockup), not one per free-text level.
 * Free-text rows all share `instructionId === 0`; they are grouped into the single Project panel.
 */
export const INSTRUCTION_PANEL_KEYS = ["Project", "Customer", "Generic"] as const;

export type InstructionPanelKey = (typeof INSTRUCTION_PANEL_KEYS)[number];

export type InstructionSeverity = "mandatory" | "important" | "generic";

export interface InstructionPanel {
  key: InstructionPanelKey;
  severity: InstructionSeverity;
  items: TaskInstruction[];
}

/** Tempo free-text levels that count as Mandatory (gate download / CAT). Order preserved inside the Project panel. */
const MANDATORY_SOURCES = ["Project", "DocumentSet", "Task"] as const;

export const hasContent = (content: string | null | undefined): content is string =>
  !!content && content.trim().length > 0;

/** Free-text project/document set/task instructions are the only ones that gate downloads and CAT links. */
export const isMandatoryInstruction = (instruction: TaskInstruction): boolean =>
  (MANDATORY_SOURCES as readonly string[]).includes(instruction.source) &&
  (hasContent(instruction.formattedText) || hasContent(instruction.description));

export const getMandatoryInstructions = (instructions: TaskInstruction[]): TaskInstruction[] =>
  instructions.filter(isMandatoryInstruction);

const isDisplayableModuleInstruction = (instruction: TaskInstruction): boolean =>
  instruction.source === "Module" &&
  (hasContent(instruction.title) || hasContent(instruction.formattedText) || hasContent(instruction.description));

/** One panel per category; empty categories are dropped. Mandatory free-text levels share the Project panel. */
export const groupInstructions = (instructions: TaskInstruction[]): InstructionPanel[] => {
  const moduleInstructions = instructions.filter(isDisplayableModuleInstruction);
  const mandatoryItems = MANDATORY_SOURCES.flatMap((source) =>
    instructions.filter((instruction) => instruction.source === source && isMandatoryInstruction(instruction))
  );

  const panels: InstructionPanel[] = [
    {
      key: "Project",
      severity: "mandatory",
      items: mandatoryItems,
    },
    {
      key: "Customer",
      severity: "important",
      items: moduleInstructions.filter((instruction) => !instruction.isGeneric),
    },
    {
      key: "Generic",
      severity: "generic",
      items: moduleInstructions.filter((instruction) => instruction.isGeneric),
    },
  ];

  return panels.filter((panel) => panel.items.length > 0);
};

/** The Mandatory (Project) panel starts expanded; Important / Generic stay closed. */
export const getDefaultOpenPanelKeys = (panels: InstructionPanel[]): InstructionPanelKey[] =>
  panels.filter((panel) => panel.severity === "mandatory").map((panel) => panel.key);
